import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { withSidebar } from "../hoc/withSidebar";
import Breadcrumbs from "../Breadcrumb/Breadcrumb";
import { Tabs, ResponsiveBox, Typography } from "../primitives";
import { useTabFocus } from "../../hooks";
import DownloadDocuments from "../AvailableDocuments/DownloadDocuments";
import BillerCrossReferenceProfileComparisonTable from "./BillerCrossReferenceProfileComparisonTable";
import ManageUsers from "../ManageUsers/ManageUsers";
import ManageBillerNewUser from "./ManageBillerNewUser";
import ManageBillerUserProfile from "./ManageBillerUserProfile";
import { MANAGE_BILLER_CROSS_REF_PROFILE_BASE } from "../../constants/ManageBillerCrossReferenceConstants";
import ERPSBaseTab from "../custom/inputs/ERPSBaseTab";
import TabPanel from "../custom/containers/TabPanel";

const BCR_BILLER_PROFILE_ROUTE = `${MANAGE_BILLER_CROSS_REF_PROFILE_BASE}/biller-profile`;

const BillerCrossReferenceProfileTab = (props) => {
  const { t } = useTranslation();
  const {
    location: { state } = {},
  } = props;

  function a11yProps(index) {
    return {
      id: `bcr-biller-profile-tab-${index}`,
      // "aria-controls": `biller-profile-tabpanel-${index}`,
    };
  }

  const [tabIndex, setTabIndex] = useState(0);
  const [hasNewUser, setHasNewUser] = useState(false);
  const [hasUserProfile, setHasUserProfile] = useState(false);
  const [fromUserProfile, setUserProfile] = useState(false);
  const [billerProfileData, setBillerProfileData] = useState({});
  const [keycloakUserStatus, setKeycloakUserStatus] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [hasViewDocuments, setHasViewDocuments] = useState(false);

  const handleTabLoading = (value) => {
    setIsLoading(value);
  };

  useTabFocus({
    activeTabIndex: tabIndex,
    tabIdPrefix: "bcr-biller-profile-tab",
    enabled: !isLoading,
  });

  const centralizedId = state?.centralizedBillerId || state?.bcrRow?.centralizedBillerId || "";
  const bcrRow = state?.bcrRow;
  const internalBillerId = bcrRow?.bmoBillerId;

  const handleTabChange = (_e, newValue) => {
    if (tabIndex === 1) {
      if (hasViewDocuments) {
        setHasViewDocuments(false);
      }
    }
    if (tabIndex === 2) {
      if (hasNewUser) {
        setHasNewUser(false);
      }
      if (hasUserProfile) {
        setHasUserProfile(false);
      }
      if (fromUserProfile) {
        setUserProfile(false);
      }
    }
    setTabIndex(newValue);
  };

  const handleClickLink = () => {
    setHasNewUser(false);
  };

  const handleClickUserProfileLink = () => {
    setHasUserProfile(false);
    setUserProfile(true);
  };

  const handleOpenUserProfile = (data, keycloakStatus) => {
    setBillerProfileData(data);
    setUserProfile(true);
    setHasUserProfile(true);
    setKeycloakUserStatus(keycloakStatus);
  };

  const handleCloseViewDocument = () => {
    setHasViewDocuments(false);
  };

  const handleOpenViewDocument = (data) => {
    setHasViewDocuments(true);
  };

  const billerComponentAccess =
    state?.componentAccess?.find(({ componentType }) => componentType === "BCRV");

  const billerProfileComponentChildAccess = billerComponentAccess?.childComponents;

  const bcrUsersTabAccess = billerProfileComponentChildAccess?.find(
    ({ componentType }) => componentType === "BMUTAB"
  );

  const renderComponent = () => {
    const updatedComponentAccess = bcrUsersTabAccess;
    if (hasNewUser) {
      return (
        <ManageBillerNewUser
          {...props}
          updateSetState={setHasNewUser}
          stateId={String(internalBillerId)}
          centralizedBillerId={centralizedId}
          routeTo={BCR_BILLER_PROFILE_ROUTE}
          handleClickLink={handleClickLink}
          pageName="biller-profile"
          componentAccess={updatedComponentAccess?.childComponents}
          parentComponentAccess={state?.componentAccess}
        />
      );
    }
    if (hasUserProfile) {
      return (
        <ManageBillerUserProfile
          {...props}
          updateSetState={setHasUserProfile}
          stateId={centralizedId}
          userId={billerProfileData.userId}
          routeTo={BCR_BILLER_PROFILE_ROUTE}
          handleClickLink={handleClickUserProfileLink}
          pageName="manage-biller"
          componentAccess={updatedComponentAccess?.childComponents}
          parentComponentAccess={state?.componentAccess}
          keycloakUserStatus={keycloakUserStatus}
        />
      );
    }
    return null;
  };


  return (
    <ResponsiveBox role={tabIndex === 1 || tabIndex === 2 ? "" : "main"}>
      <ResponsiveBox ml={3}>
        <Breadcrumbs
          {...props}
          componentAccess={state?.componentAccess}
          fromDetailPage={true}
        />

        <ResponsiveBox mt={2}>
          <Typography variant="h1">
            {t("erps.billermanagement.crossreference.label.centralizedBillerId")}{" "}
            : {centralizedId}
          </Typography>
        </ResponsiveBox>

        <ResponsiveBox sx={{ width: "100%" }}>
          <ResponsiveBox sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              aria-label="biller-cross-reference-biller-tabs"
              textColor="primary"
            >
              <ERPSBaseTab
                label={t("erps.editbiller.tabs.label.profile")}
                {...a11yProps(0)}
                setTabIndex={setTabIndex}
                componentAccess={billerProfileComponentChildAccess}
                actionType={"hasBillerProfileTabAccess"}
              />
              <ERPSBaseTab
                label={t("erps.editbiller.tabs.label.availabledocuments")}
                {...a11yProps(1)}
                setTabIndex={setTabIndex}
                componentAccess={billerProfileComponentChildAccess}
                actionType={"hasBillerAvlDocTabAccess"}
                disabled={isLoading}
              />
              <ERPSBaseTab
                label={t("erps.editbiller.tabs.label.users")}
                {...a11yProps(2)}
                setTabIndex={setTabIndex}
                componentAccess={billerProfileComponentChildAccess}
                actionType={"hasManageBillerUserTabAccess"}
                disabled={isLoading}
              />
            </Tabs>
          </ResponsiveBox>

          <TabPanel
              value={tabIndex}
              index={0}
              componentAccess={billerProfileComponentChildAccess}
              actionType={"hasBillerProfileTabAccess"}
          >
            {bcrRow ? (
                <BillerCrossReferenceProfileComparisonTable rowData={bcrRow} />
              ) : (
                <ResponsiveBox sx={{ p: 2 }}>
                  <Typography variant="body1">
                    {t("erps.billermanagement.crossreference.profile.noRowData")}
                  </Typography>
                </ResponsiveBox>
              )}
          </TabPanel>
          <TabPanel
              value={tabIndex}
              index={1}
              componentAccess={billerProfileComponentChildAccess}
              actionType={"hasBillerAvlDocTabAccess"}
          >
            <DownloadDocuments
                {...props}
                pageName="biller-profile"
                billerId={internalBillerId}
                centralizedBillerId={centralizedId}
                stateId={centralizedId}
                parentComponentAccess={state?.componentAccess}
                pathValue={"/manage-biller-cross-reference/view-documents"}
                checkRedirect={true}
                handleClickLink={handleCloseViewDocument}
                handleCloseViewDocument={handleCloseViewDocument}
                handleOpenViewDocument={handleOpenViewDocument}
                isBillerCrossReferenceProfile={true}
              />
          </TabPanel>
          <TabPanel
              value={tabIndex}
              index={2}
              mt={-4}
              ml={-3}
              componentAccess={billerProfileComponentChildAccess}
              actionType={"hasManageBillerUserTabAccess"}
          >
            {!hasNewUser && !hasUserProfile ? (
                <ManageUsers
                    {...props}
                    pageName="biller-profile"
                    stateId={centralizedId}
                    billerId={internalBillerId}
                    centralizedBillerId={centralizedId}
                    padAccepted={bcrRow?.padAccepted}
                    setHasNewUser={setHasNewUser}
                    handleOpenUserProfile={handleOpenUserProfile}
                    handleTabLoading={handleTabLoading}
                    parentComponentAccess={state?.componentAccess}
                    componentAccess={billerProfileComponentChildAccess}
                    fromUserProfile={fromUserProfile}
                    isBillerCrossReferenceProfile={true}
                  />
                ) : (
                  renderComponent()
                )}
          </TabPanel>
        </ResponsiveBox>
      </ResponsiveBox>
    </ResponsiveBox>
  );
};

export default withSidebar(BillerCrossReferenceProfileTab);
